<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :items-per-page="value.itemsPerPage"
    :mobile-breakpoint="0"
    :sort-by="value.sortBy"
    :sort-desc="value.sortDesc"
    :page="value.page"
    class="dashboard-table"
    :loading="filterLoading"
    show-select
    item-key="_key"
    :server-items-length="serverItemsLength"
    :hide-default-header="$vuetify.breakpoint.mdAndDown"
    :value="selectedKeys.map((item) => ({ _key: item }))"
    :footer-props="{ itemsPerPageText: 'Per page:', itemsPerPageOptions: value.itemsPerPageOptions.map(item => item.value) }"
    @update:page="updateModel({ page: $event })"
    @update:sort-by="updateModel({ sortBy: $event })"
    @update:sort-desc="updateModel({ sortDesc: $event })"
    @update:items-per-page="updateModel({ itemsPerPage: $event })"
    @toggle-select-all="onToggleSelectAll"
  >
    <template #item="{ item, index }">
      <OpenInvoiceMobileCard
        v-if="$vuetify.breakpoint.mdAndDown"
        :item="item"
        :active-account-id="activeAccountId"
        :class="index === 0 ? 'mt-0' : 'mt-2'"
        :selected="selectedKeys.includes(item._key)"
        @click:select="changeSelectHandler(item._key)"
      />
      <tr v-else class="sc-broker-ranking-broker">
        <td class="sc-data-list-select">
          <v-checkbox
            primary
            hide-details
            class="mt-0 pt-0"
            :input-value="selectedKeys.includes(item._key)"
            @change="changeSelectHandler(item._key)"
          />
        </td>
        <td class="sc-data-list-select" style="width: 80px">
          <CommonChip class="sc-nbr" dark>
            {{ index + 1 }}
          </CommonChip>
        </td>
        <td class="sc-data-list-select" style="width: 80px">
          <span>{{ item.invoice_number }}</span>
        </td>
        <td class="sc-data-list" style="min-width: 300px">
          <router-link
            class="font-weight-bold primary--text text-decoration-none"
            :to="getAccountRoute({ name: 'deal', params: { id: item.deal._key }})"
          >
            {{ item.deal.name }}
          </router-link>
        </td>
        <td class="sc-data-list" style="min-width: 200px">
          <Label :value="getBrokers(item.deal)" />
        </td>
        <td class="sc-data-list">
          <Label :value="getCompanyNames(item.buyer_tenant)" />
        </td>
        <td class="sc-data-list">
          <Label :value="getCompanyNames(item.seller_landlord)" />
        </td>
        <td class="sc-data-list">
          <Label :value="item.due_date ? $options.filters.formatDate(item.due_date) : null" />
        </td>
        <td class="sc-data-list">
          {{ item.amount | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.amount_paid | formatCurrency }}
        </td>
        <td class="sc-data-list">
          {{ item.amount_outstanding | formatCurrency }}
        </td>
        <td class="sc-data-list">
          <span :style="`color: ${item.color}`">{{ item.status | capitalize }}</span>
        </td>
        <td class="sc-data-list" style="min-width: 110px">
          <Label :value="item.diffLabel" />
        </td>
        <td class="sc-data-list">
          <Label :value="item.daysDiff" />
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { PropType } from 'vue';
import mixins from 'vue-typed-mixins';
import { capitalize } from 'lodash-es';

import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import Label from '@/bundles/Common/components/Label.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import OpenInvoiceMobileCard from '@/bundles/Dashboard/components/OpenInvoiceMobileCard.vue';

interface IOpenInvoicesTableData {
  headers?: IHeader[];
}

const companyNameSortFunc = (a: { name: string }[], b: { name: string }[]) => {
  const aJoin = a.map(item => item.name).join(', ');
  const bJoin = b.map(item => item.name).join(', ');

  if (aJoin && bJoin) {
    return aJoin.localeCompare(bJoin);
  }

  return 0;
};

const headers = [
  { text: '#', value: '#', sortable: false },
  { text: 'Invoice No.', value: 'invoice_number', sortable: true, class: '', icon: 'number' },
  { text: 'Deal Name', value: 'deal.name', sortable: true, class: '', icon: 'string' },
  { text: `${capitalize(i18n.tc(LocalizationEnum.broker))}(s)`, value: 'broker', sortable: false, class: '', icon: 'string' },
  { text: 'Buyer/Tenant', value: 'buyer_tenant', sortable: true, class: '', icon: 'string', sort: companyNameSortFunc },
  { text: 'Seller/Landlord', value: 'seller_landlord', sortable: true, class: '', icon: 'string', sort: companyNameSortFunc },
  { text: 'Due Date', value: 'due_date', sortable: true, class: '', icon: 'date' },
  { text: 'Invoice Amount ($)', value: 'amount', sortable: true, class: '', icon: 'number' },
  { text: 'Amount Paid ($)', value: 'amount_paid', sortable: true, class: '', icon: 'number' },
  { text: 'Outstanding ($)', value: 'amount_outstanding', sortable: true, class: '', icon: 'number' },
  { text: 'Payment Status', value: 'status', sortable: true, class: '', icon: 'string' },
  { text: 'Aging', value: 'diffLabel', sortable: true, class: '', icon: 'number' },
  { text: '# Days Aged', value: 'daysDiff', sortable: true, class: '', icon: 'number' }
]

export default mixins(AccountLayoutMixin).extend({
  name: 'OpenInvoicesTable',

  components: {
    OpenInvoiceMobileCard,
    CommonChip,
    Label
  },

  props: {
    value: {
      type: Object as PropType<ITableOptions>,
      required: true
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    filterLoading: Boolean,
    selectedKeys: {
      type: Array as PropType<string[]>,
      default: [],
    },
    serverItemsLength: {
      type: Number,
      default: -1,
    }
  },

  data: (): IOpenInvoicesTableData => ({}),

  created () {
    this.headers = headers;
  },

  methods: {
    getCompanyNames (contacts: { name: string }[]): string {
      return contacts.map(item => item.name).join(', ') || '';
    },
    getBrokers (deal) {
      return deal.brokers.map(item => `${item.first_name} ${item.last_name}`).join(', ');
    },
    updateModel (model: Partial<ITableOptions>) {
      this.$emit('input', { ...this.value, ...model });
    },
    changeSelectHandler (key: string) {
      const selectedKeys = this.selectedKeys;

      const payload = selectedKeys.includes(key) ? selectedKeys.filter(item => item !== key) : [...selectedKeys, key];
      this.$emit('update:selectedKeys', payload);
    },
    onToggleSelectAll ({ value }: { value: boolean }) {
      const selectedKeys = value ? this.items.map(item => item._key) : [];
      this.$emit('update:selectedKeys', selectedKeys);
    }
  }
})
</script>
